import { useMemo, useState } from "react";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import type { BoardRecord } from "../types";
import { Modal } from "./Modal";

interface BoardManagerProps {
  boards: BoardRecord[];
  activeBoardId: string;
  onClose: () => void;
  onCreate: (name: string) => void;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}

export function BoardManager(props: BoardManagerProps) {
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<string>();
  const [renameValue, setRenameValue] = useState("");
  const sortedBoards = useMemo(
    () => [...props.boards].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [props.boards],
  );

  const submitNew = () => {
    const name = newName.trim();
    if (!name) return;
    props.onCreate(name);
    setNewName("");
  };

  return (
    <Modal
      description="Semua papan disimpan pada peranti ini sahaja."
      onClose={props.onClose}
      title="Papan Saya"
      wide
    >
      <form
        className="create-board"
        onSubmit={(event) => {
          event.preventDefault();
          submitNew();
        }}
      >
        <input
          aria-label="Nama papan baharu"
          maxLength={80}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="Contoh: Funnel Kempen Ogos"
          value={newName}
        />
        <button className="button button--primary" disabled={!newName.trim()} type="submit">
          <Plus size={16} /> Papan baharu
        </button>
      </form>

      <div className="board-list">
        {sortedBoards.map((board) => {
          const active = board.id === props.activeBoardId;
          const isRenaming = renamingId === board.id;
          return (
            <article className={`board-row${active ? " board-row--active" : ""}`} key={board.id}>
              <button className="board-row__main" onClick={() => props.onSelect(board.id)} type="button">
                <span className="board-thumb" aria-hidden="true"><i /><i /><i /></span>
                <span>
                  {isRenaming ? (
                    <input
                      autoFocus
                      maxLength={80}
                      onBlur={() => {
                        if (renameValue.trim()) props.onRename(board.id, renameValue);
                        setRenamingId(undefined);
                      }}
                      onChange={(event) => setRenameValue(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                        if (event.key === "Escape") setRenamingId(undefined);
                      }}
                      value={renameValue}
                    />
                  ) : (
                    <strong>{board.name}</strong>
                  )}
                  <small>{active ? "Sedang dibuka" : `Dikemas kini ${new Date(board.updatedAt).toLocaleString("ms-MY")}`}</small>
                </span>
              </button>
              <div className="board-row__actions">
                <button
                  aria-label={`Namakan semula ${board.name}`}
                  className="icon-button"
                  onClick={() => {
                    setRenamingId(board.id);
                    setRenameValue(board.name);
                  }}
                  type="button"
                ><Pencil size={16} /></button>
                <button aria-label={`Salin ${board.name}`} className="icon-button" onClick={() => props.onDuplicate(board.id)} type="button">
                  <Copy size={16} />
                </button>
                <button
                  aria-label={`Padam ${board.name}`}
                  className="icon-button icon-button--danger"
                  disabled={props.boards.length === 1}
                  onClick={() => props.onDelete(board.id)}
                  type="button"
                ><Trash2 size={16} /></button>
              </div>
            </article>
          );
        })}
      </div>
    </Modal>
  );
}

